import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import cx from 'classnames';
import _ from '@/utils/lodash';
import { useAppContext } from '@/context/AppContext';
import { getPagePath } from '@/utils/pagePaths';
import ResourcePageGroup from '@components/resources/ResourcePageGroup';
import { getBestAvailableUrl } from '@components/shared/OptimizedImage';
import notify from '@/services/notifications';

/**
 * Resources Page - displays all images grouped by pages
 * Route: /resources
 * Allows users to select images and download as ZIP or PDF
 * Protected by password authentication
 */
function ResourcesPage() {
    const navigate = useNavigate();
    const { content } = useAppContext();
    const pages = content?.pages || {};
    const files = content?.files || {};
    const properties = content?.properties || {};
    const home = content?.home || {};
    
    // Authentication state
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authChecking, setAuthChecking] = useState(true);
    const [sessionExpiresAt, setSessionExpiresAt] = useState(null);
    const [allowedPages, setAllowedPages] = useState(null); // null = all pages, array = specific pages
    
    // Check authentication on mount and periodically
    useEffect(() => {
        let isMounted = true;
        let checkInterval = null;
        let fastCheckInterval = null;
        
        const checkAuth = async () => {
            const token = localStorage.getItem('resources-session-token');
            if (!token) {
                // No token, redirect to login
                if (isMounted) {
                    setIsAuthenticated(false);
                    navigate('/resources/login');
                }
                return;
            }

            try {
                const response = await fetch(`/api/resources/auth?token=${encodeURIComponent(token)}`);
                const data = await response.json();
                
                if (!isMounted) return;
                
                if (data.authorized) {
                    setIsAuthenticated(true);
                    setSessionExpiresAt(data.expiresAt);
                    // Store allowedPages from session
                    setAllowedPages(data.allowedPages !== undefined ? data.allowedPages : null);
                    setAuthChecking(false);
                } else {
                    // Token invalid or revoked, clear and redirect
                    localStorage.removeItem('resources-session-token');
                    setIsAuthenticated(false);
                    setAuthChecking(false);
                    navigate('/resources/login');
                }
            } catch (err) {
                console.error('Auth check failed:', err);
                if (isMounted) {
                    localStorage.removeItem('resources-session-token');
                    setIsAuthenticated(false);
                    setAuthChecking(false);
                    navigate('/resources/login');
                }
            }
        };

        // Initial check
        checkAuth();
        
        // Fast polling when page is visible (every 30 seconds for near real-time detection)
        const startFastPolling = () => {
            if (fastCheckInterval) clearInterval(fastCheckInterval);
            fastCheckInterval = setInterval(checkAuth, 30 * 1000); // 30 seconds
        };
        
        const stopFastPolling = () => {
            if (fastCheckInterval) {
                clearInterval(fastCheckInterval);
                fastCheckInterval = null;
            }
        };
        
        // Start fast polling if page is visible
        if (document.visibilityState === 'visible') {
            startFastPolling();
        }
        
        // Handle visibility changes
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                // Page became visible - check immediately and start fast polling
                checkAuth();
                startFastPolling();
            } else {
                // Page hidden - stop fast polling to save resources
                stopFastPolling();
            }
        };
        
        // Handle window focus (user switches back to tab)
        const handleFocus = () => {
            checkAuth();
            if (document.visibilityState === 'visible') {
                startFastPolling();
            }
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        
        // Fallback: slower polling every 5 minutes (in case visibility API isn't supported)
        checkInterval = setInterval(checkAuth, 5 * 60 * 1000);
        
        return () => {
            isMounted = false;
            if (checkInterval) clearInterval(checkInterval);
            if (fastCheckInterval) clearInterval(fastCheckInterval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, [navigate]);
    
    // Handle logout
    const handleLogout = useCallback(async () => {
        const token = localStorage.getItem('resources-session-token');
        if (token) {
            try {
                await fetch('/api/resources/auth', {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token })
                });
            } catch (err) {
                console.error('Logout request failed:', err);
            }
        }
        localStorage.removeItem('resources-session-token');
        navigate('/resources/login');
    }, [navigate]);
    
    // Helper to get session token
    const getSessionToken = useCallback(() => {
        return localStorage.getItem('resources-session-token');
    }, []);
    
    // Helper to handle 401 responses (session invalid)
    const handleAuthError = useCallback(() => {
        localStorage.removeItem('resources-session-token');
        setIsAuthenticated(false);
        navigate('/resources/login');
    }, [navigate]);
    
    // State for selections
    const [selectedPages, setSelectedPages] = useState(new Set());
    const [selectedImages, setSelectedImages] = useState(new Set());
    const [isDownloading, setIsDownloading] = useState(false);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [webpSizes, setWebpSizes] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    
    // Load selection from localStorage on mount
    useEffect(() => {
        if (!isAuthenticated) return;
        try {
            const savedImages = localStorage.getItem('resources-selected-images');
            const savedPages = localStorage.getItem('resources-selected-pages');
            if (savedImages) {
                const imageIds = JSON.parse(savedImages);
                if (Array.isArray(imageIds) && imageIds.length > 0) {
                    setSelectedImages(new Set(imageIds));
                }
            }
            if (savedPages) {
                const pageIds = JSON.parse(savedPages);
                if (Array.isArray(pageIds) && pageIds.length > 0) {
                    setSelectedPages(new Set(pageIds));
                }
            }
        } catch (error) {
            console.error('Failed to load selection from localStorage:', error);
        }
    }, [isAuthenticated]);
    
    // Save selection to localStorage whenever it changes
    useEffect(() => {
        if (!isAuthenticated) return;
        try {
            localStorage.setItem('resources-selected-images', JSON.stringify(Array.from(selectedImages)));
            localStorage.setItem('resources-selected-pages', JSON.stringify(Array.from(selectedPages)));
        } catch (error) {
            console.error('Failed to save selection to localStorage:', error);
        }
    }, [selectedImages, selectedPages, isAuthenticated]);
    
    // Build page-to-images data structure
    const pageData = useMemo(() => {
        if (!isAuthenticated || allowedPages === undefined) {
            return []; // Wait for auth to complete
        }
        
        const result = [];
        
        // Determine if home page should be included
        const includeHome = allowedPages === null || (Array.isArray(allowedPages) && allowedPages.includes('home'));
        
        // Helper function to extract image IDs from gallery items (handles spreads, objects, and strings)
        const extractImageIds = (items) => {
            const imageIds = [];
            for (const item of items) {
                if (Array.isArray(item) && item.length === 2) {
                    // Spread - extract both images
                    for (const spreadImg of item) {
                        const imgId = typeof spreadImg === 'string' ? spreadImg : (spreadImg?.id || null);
                        if (imgId && typeof imgId === 'string') {
                            imageIds.push(imgId);
                        }
                    }
                } else if (typeof item === 'string') {
                    // Simple string ID
                    imageIds.push(item);
                } else if (typeof item === 'object' && item !== null && item.id) {
                    // Object with id property
                    if (typeof item.id === 'string') {
                        imageIds.push(item.id);
                    }
                }
            }
            return imageIds;
        };
        
        // Add home page if allowed and has images
        if (includeHome) {
            const homeImages = home.gallery?.images || [];
            if (homeImages.length > 0) {
                // Extract all image IDs (including from spreads)
                const homeImageIds = extractImageIds(homeImages);
                // Map imageIds to files, keeping only valid pairs
                const homeImagePairs = homeImageIds
                    .map(imgId => ({ imageId: imgId, file: files[imgId] }))
                    .filter(pair => pair.file); // Only keep pairs where file exists
                
                if (homeImagePairs.length > 0) {
                    result.push({
                        pageId: 'home',
                        pageName: home.name || 'Home',
                        pagePath: '/',
                        images: homeImagePairs.map(pair => pair.file),
                        imageIds: homeImagePairs.map(pair => pair.imageId),
                    });
                }
            }
        }
        
        // Helper to get page identifier
        const getPageId = (page) => page.id || page._id;
        const getParentId = (page) => page.parentId || page.parentUrl;
        
        // Build hierarchical page tree (same as admin menu)
        const buildPageTree = (pagesObj) => {
            if (!pagesObj || Object.keys(pagesObj).length === 0) return [];
            
            const pagesMap = new Map();
            const rootPages = [];
            
            // First pass: create map and identify root pages
            Object.values(pagesObj).forEach(page => {
                const pageId = getPageId(page);
                pagesMap.set(pageId, { ...page, children: [] });
                if (!getParentId(page)) {
                    rootPages.push(pageId);
                }
            });
            
            // Second pass: build tree structure
            pagesMap.forEach((page, pageId) => {
                const parentId = getParentId(page);
                if (parentId && pagesMap.has(parentId)) {
                    pagesMap.get(parentId).children.push(page);
                }
            });
            
            // Sort root pages by ord
            const sortedRoots = rootPages
                .map(id => pagesMap.get(id))
                .sort((a, b) => (a.ord || 0) - (b.ord || 0));
            
            return sortedRoots;
        };
        
        // Flatten tree in hierarchical order (parent, then children recursively)
        const flattenTree = (treePages) => {
            const flattened = [];
            for (const page of treePages) {
                flattened.push(page);
                if (page.children && page.children.length > 0) {
                    // Sort children by ord and recursively flatten
                    const sortedChildren = page.children.sort((a, b) => (a.ord || 0) - (b.ord || 0));
                    flattened.push(...flattenTree(sortedChildren));
                }
            }
            return flattened;
        };
        
        // Filter pages based on allowedPages
        let pagesList;
        if (allowedPages === null) {
            // All pages allowed - build tree and flatten
            const pageTree = buildPageTree(pages);
            pagesList = flattenTree(pageTree);
        } else if (Array.isArray(allowedPages)) {
            // Only specific pages allowed - build full tree first, then filter
            const allowedSet = new Set(allowedPages);
            
            // Build full tree to maintain parent-child relationships
            const fullPageTree = buildPageTree(pages);
            
            // Filter tree to only include allowed pages (but keep structure)
            const filterTree = (treePages) => {
                const filtered = [];
                for (const page of treePages) {
                    const pageId = getPageId(page);
                    if (allowedSet.has(pageId)) {
                        // This page is allowed - include it and filter its children
                        const filteredPage = { ...page };
                        if (page.children && page.children.length > 0) {
                            filteredPage.children = filterTree(page.children);
                        }
                        filtered.push(filteredPage);
                    } else {
                        // This page is not allowed, but check its children
                        if (page.children && page.children.length > 0) {
                            const filteredChildren = filterTree(page.children);
                            if (filteredChildren.length > 0) {
                                // Some children are allowed - include them as root-level items
                                filtered.push(...filteredChildren);
                            }
                        }
                    }
                }
                return filtered;
            };
            
            const filteredTree = filterTree(fullPageTree);
            pagesList = flattenTree(filteredTree);
        } else {
            // Invalid state, no pages
            pagesList = [];
        }
        
        // Add all allowed pages (even if they have no images)
        for (const page of pagesList) {
            const pageImages = page.gallery?.images || [];
            const pagePath = getPagePath(page, pages);
            
            // Extract image IDs and files if images exist
            let imagePairs = [];
            if (pageImages.length > 0) {
                // Extract all image IDs (including from spreads)
                const pageImageIds = extractImageIds(pageImages);
                // Map imageIds to files, keeping only valid pairs
                imagePairs = pageImageIds
                    .map(imgId => ({ imageId: imgId, file: files[imgId] }))
                    .filter(pair => pair.file); // Only keep pairs where file exists
            }
            
            // Add page regardless of whether it has images
            result.push({
                pageId: page.id || page._id,
                pageName: page.name || 'Untitled',
                pagePath: pagePath ? `/${pagePath}` : '#',
                images: imagePairs.map(pair => pair.file),
                imageIds: imagePairs.map(pair => pair.imageId),
            });
        }
        
        return result;
    }, [pages, files, home, isAuthenticated, allowedPages]);
    
    // Filter pageData based on search query
    const filteredPageData = useMemo(() => {
        if (!searchQuery.trim()) {
            return pageData;
        }
        
        const query = searchQuery.toLowerCase().trim();
        
        return pageData
            .map(page => {
                // Check if page name matches
                const pageNameMatch = page.pageName.toLowerCase().includes(query);
                
                // Filter images that match the search
                const matchingImages = [];
                const matchingImageIds = [];
                
                page.images.forEach((file, index) => {
                    const imageId = page.imageIds[index];
                    const description = (file?.description || '').toLowerCase();
                    const imageIdMatch = imageId.toLowerCase().includes(query);
                    const descriptionMatch = description.includes(query);
                    
                    if (pageNameMatch || imageIdMatch || descriptionMatch) {
                        matchingImages.push(file);
                        matchingImageIds.push(imageId);
                    }
                });
                
                // If page name matches or has matching images, include the page
                if (pageNameMatch || matchingImages.length > 0) {
                    return {
                        ...page,
                        images: matchingImages,
                        imageIds: matchingImageIds,
                    };
                }
                
                return null;
            })
            .filter(page => page !== null);
    }, [pageData, searchQuery]);
    
    // Handle page selection (with 50 image limit)
    const handlePageSelect = useCallback((pageId, isSelected) => {
        setSelectedPages(prev => {
            const next = new Set(prev);
            if (isSelected) {
                next.add(pageId);
            } else {
                next.delete(pageId);
            }
            return next;
        });
        
        // Auto-select/deselect all images in the page
        const page = pageData.find(p => p.pageId === pageId);
        if (page) {
            setSelectedImages(prev => {
                const next = new Set(prev);
                if (isSelected) {
                    // Check if adding this page would exceed the limit
                    const newCount = prev.size + page.imageIds.length;
                    if (newCount > 50) {
                        notify.error(`Cannot select this page. Selection limit is 50 images (currently ${prev.size} selected, this page has ${page.imageIds.length} images).`);
                        return prev;
                    }
                    for (const imgId of page.imageIds) {
                        next.add(imgId);
                    }
                } else {
                    for (const imgId of page.imageIds) {
                        next.delete(imgId);
                    }
                }
                return next;
            });
        }
    }, [pageData]);
    
    // Handle individual image selection (with 50 image limit)
    const handleImageSelect = useCallback((imageId, isSelected) => {
        setSelectedImages(prev => {
            const next = new Set(prev);
            if (isSelected) {
                // Check selection limit
                if (next.size >= 50) {
                    notify.error('Selection limit reached. Maximum 50 images can be selected.');
                    return prev;
                }
                next.add(imageId);
            } else {
                next.delete(imageId);
            }
            
            // Check if all images in a page are selected, then select the page
            setSelectedPages(currentPages => {
                const nextPages = new Set(currentPages);
                for (const page of pageData) {
                    const allSelected = page.imageIds.length > 0 && 
                        page.imageIds.every(imgId => next.has(imgId));
                    
                    if (allSelected) {
                        nextPages.add(page.pageId);
                    } else {
                        nextPages.delete(page.pageId);
                    }
                }
                return nextPages;
            });
            
            return next;
        });
    }, [pageData]);
    
    // Calculate selection stats (using WebP file sizes)
    const selectionStats = useMemo(() => {
        const count = selectedImages.size;
        let totalSize = 0;
        
        for (const imgId of selectedImages) {
            // Use WebP file size if available, otherwise fall back to original size
            const webpSizeInfo = webpSizes[imgId];
            if (webpSizeInfo?.size) {
                totalSize += webpSizeInfo.size;
            } else {
                // Fallback to original file size if WebP size not yet loaded
                const file = files[imgId];
                if (file?.size) {
                    totalSize += file.size;
                }
            }
        }
        
        return { count, totalSize };
    }, [selectedImages, webpSizes, files]);
    
    // Format file size
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
    };
    
    // Handle download ZIP
    const handleDownloadZip = useCallback(async () => {
        if (selectedImages.size === 0 || isDownloading) return;
        
        // Warn for large selections
        if (selectedImages.size > 50) {
            const confirmed = window.confirm(
                `You are about to download ${selectedImages.size} images. This may take a while. Continue?`
            );
            if (!confirmed) return;
        }
        
        setIsDownloading(true);
        
        try {
            const token = getSessionToken();
            if (!token) {
                handleAuthError();
                return;
            }
            
            const response = await fetch(`/api/resources/download?token=${encodeURIComponent(token)}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ imageIds: Array.from(selectedImages) }),
            });
            
            if (response.status === 401) {
                handleAuthError();
                return;
            }
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Download failed');
            }
            
            const blob = await response.blob();
            // Extract filename from Content-Disposition header, or use site title
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = `resources-${Date.now()}.zip`;
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            } else {
                // Fallback: use site title
                const siteTitle = properties.site_title || 'resources';
                const sanitized = siteTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
                filename = `${sanitized}-${Date.now()}.zip`;
            }
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download error:', error);
            notify.error(error.message || 'Failed to download resources. Please try again.');
        } finally {
            setIsDownloading(false);
        }
    }, [selectedImages, isDownloading, getSessionToken, handleAuthError]);
    
    // Handle generate PDF
    const handleGeneratePdf = useCallback(async () => {
        if (selectedImages.size === 0 || isGeneratingPdf) return;
        
        // Warn for large selections
        if (selectedImages.size > 30) {
            const confirmed = window.confirm(
                `You are about to generate a PDF with ${selectedImages.size} images. This may take a while. Continue?`
            );
            if (!confirmed) return;
        }
        
        setIsGeneratingPdf(true);
        
        try {
            const token = getSessionToken();
            if (!token) {
                handleAuthError();
                return;
            }
            
            const response = await fetch(`/api/resources/pdf?token=${encodeURIComponent(token)}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ imageIds: Array.from(selectedImages) }),
            });
            
            if (response.status === 401) {
                handleAuthError();
                return;
            }
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'PDF generation failed');
            }
            
            const blob = await response.blob();
            // Extract filename from Content-Disposition header, or use site title
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = `resources-${Date.now()}.pdf`;
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            } else {
                // Fallback: use site title
                const siteTitle = properties.site_title || 'resources';
                const sanitized = siteTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
                filename = `${sanitized}-${Date.now()}.pdf`;
            }
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('PDF generation error:', error);
            notify.error(error.message || 'Failed to generate PDF. Please try again.');
        } finally {
            setIsGeneratingPdf(false);
        }
    }, [selectedImages, isGeneratingPdf, getSessionToken, handleAuthError]);
    
    // Deselect all
    const handleDeselectAll = useCallback(() => {
        setSelectedImages(new Set());
        setSelectedPages(new Set());
    }, []);
    
    // Resources has no per-page colours, so it simply follows the site theme.
    const themeBackground = 'var(--color-background)';
    const themeText = 'var(--color-text)';
    
    // State for preview panel
    const [previewFile, setPreviewFile] = useState(null);
    const [previewImageId, setPreviewImageId] = useState(null);
    
    // Handle image hover for preview
    const handleImageHover = useCallback((file, imageId) => {
        setPreviewFile(file);
        setPreviewImageId(imageId);
    }, []);
    
    // Handle image focus for preview (keyboard navigation)
    const handleImageFocus = useCallback((file, imageId) => {
        setPreviewFile(file);
        setPreviewImageId(imageId);
    }, []);
    
    // Fetch webp file sizes for all images
    useEffect(() => {
        if (!isAuthenticated) return;
        const allImageIds = pageData.flatMap(page => page.imageIds);
        if (allImageIds.length === 0) return;
        
        const token = getSessionToken();
        if (!token) {
            handleAuthError();
            return;
        }
        
        fetch(`/api/resources/file-sizes?token=${encodeURIComponent(token)}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ imageIds: allImageIds }),
        })
            .then(res => {
                if (res.status === 401) {
                    handleAuthError();
                    return null;
                }
                return res.json();
            })
            .then(data => {
                if (data && data.sizes) {
                    setWebpSizes(data.sizes);
                }
            })
            .catch(error => {
                console.error('Failed to fetch webp file sizes:', error);
            });
    }, [pageData, isAuthenticated, getSessionToken, handleAuthError]);
    
    // State for logo URL (try multiple paths)
    const [logoUrl, setLogoUrl] = useState('/assets/logo.svg');
    
    // Try to load logo from multiple locations
    useEffect(() => {
        const tryLogoPaths = ['/assets/logo.svg', '/logo.svg'];
        let currentIndex = 0;
        
        const tryNextLogo = () => {
            if (currentIndex >= tryLogoPaths.length) {
                setLogoUrl(null); // No logo found
                return;
            }
            
            const img = new Image();
            img.onload = () => {
                setLogoUrl(tryLogoPaths[currentIndex]);
            };
            img.onerror = () => {
                currentIndex++;
                tryNextLogo();
            };
            img.src = tryLogoPaths[currentIndex];
        };
        
        tryNextLogo();
    }, []);
    
    // Get preview image URL (prefer larger size for preview)
    // Uses getBestAvailableUrl which properly handles fallbacks based on actual file availability
    const getPreviewImageUrl = (file) => {
        if (!file) return null;
        // Request 1920px, but getBestAvailableUrl will automatically fall back to smaller sizes if not available
        const url = getBestAvailableUrl(file, 1920);
        return url || null;
    };
    
    // Get all focusable tile elements
    const getFocusableTiles = useCallback(() => {
        // Get all tiles by data attribute
        return Array.from(document.querySelectorAll('[data-resource-tile]'));
    }, []);
    
    // Find tile index in the flat array
    const getTileIndex = useCallback((tileElement) => {
        const tiles = getFocusableTiles();
        return tiles.indexOf(tileElement);
    }, [getFocusableTiles]);
    
    // Navigate to adjacent tile based on direction
    const navigateToTile = useCallback((direction) => {
        const activeElement = document.activeElement;
        if (!activeElement || !activeElement.hasAttribute('data-resource-tile')) {
            return;
        }
        
        const tiles = getFocusableTiles();
        if (tiles.length === 0) return;
        
        const currentRect = activeElement.getBoundingClientRect();
        const currentCenterX = currentRect.left + currentRect.width / 2;
        const currentCenterY = currentRect.top + currentRect.height / 2;
        
        let bestTile = null;
        let bestDistance = Infinity;
        const threshold = 50; // pixels tolerance for same row/column detection
        
        for (const tile of tiles) {
            if (tile === activeElement) continue;
            
            const tileRect = tile.getBoundingClientRect();
            const tileCenterX = tileRect.left + tileRect.width / 2;
            const tileCenterY = tileRect.top + tileRect.height / 2;
            
            let isCandidate = false;
            let distance = Infinity;
            
            switch (direction) {
                case 'ArrowUp':
                    // Find tile above (same column, higher Y)
                    if (tileCenterY < currentCenterY && 
                        Math.abs(tileCenterX - currentCenterX) < threshold) {
                        isCandidate = true;
                        distance = currentCenterY - tileCenterY;
                    }
                    break;
                case 'ArrowDown':
                    // Find tile below (same column, lower Y)
                    if (tileCenterY > currentCenterY && 
                        Math.abs(tileCenterX - currentCenterX) < threshold) {
                        isCandidate = true;
                        distance = tileCenterY - currentCenterY;
                    }
                    break;
                case 'ArrowLeft':
                    // Find tile to the left (same row, lower X)
                    if (tileCenterX < currentCenterX && 
                        Math.abs(tileCenterY - currentCenterY) < threshold) {
                        isCandidate = true;
                        distance = currentCenterX - tileCenterX;
                    }
                    break;
                case 'ArrowRight':
                    // Find tile to the right (same row, higher X)
                    if (tileCenterX > currentCenterX && 
                        Math.abs(tileCenterY - currentCenterY) < threshold) {
                        isCandidate = true;
                        distance = tileCenterX - currentCenterX;
                    }
                    break;
            }
            
            if (isCandidate && distance < bestDistance) {
                bestDistance = distance;
                bestTile = tile;
            }
        }
        
        // If no tile found in the exact direction, fall back to simple index-based navigation
        if (!bestTile) {
            const currentIndex = getTileIndex(activeElement);
            if (currentIndex === -1) return;
            
            // Estimate columns by checking how many tiles are in the first row
            let columns = 1;
            const firstTileRect = tiles[0].getBoundingClientRect();
            for (let i = 1; i < tiles.length; i++) {
                const tileRect = tiles[i].getBoundingClientRect();
                if (Math.abs(tileRect.top - firstTileRect.top) < 10) {
                    columns++;
                } else {
                    break;
                }
            }
            
            let nextIndex = currentIndex;
            switch (direction) {
                case 'ArrowUp':
                    nextIndex = Math.max(0, currentIndex - columns);
                    break;
                case 'ArrowDown':
                    nextIndex = Math.min(tiles.length - 1, currentIndex + columns);
                    break;
                case 'ArrowLeft':
                    nextIndex = Math.max(0, currentIndex - 1);
                    break;
                case 'ArrowRight':
                    nextIndex = Math.min(tiles.length - 1, currentIndex + 1);
                    break;
            }
            
            if (nextIndex !== currentIndex && tiles[nextIndex]) {
                bestTile = tiles[nextIndex];
            }
        }
        
        if (bestTile) {
            bestTile.focus();
            // Scroll into view if needed
            bestTile.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [getFocusableTiles, getTileIndex]);
    
    // Keyboard shortcuts
    useEffect(() => {
        if (!isAuthenticated) return;
        
        const handleKeyDown = (e) => {
            // Arrow keys for navigation
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                const activeElement = document.activeElement;
                // If a tile is focused, navigate
                if (activeElement && activeElement.hasAttribute('data-resource-tile')) {
                    e.preventDefault();
                    navigateToTile(e.key);
                    return;
                }
                
                // If no tile is focused but user pressed arrow key, focus the first tile
                const tiles = getFocusableTiles();
                if (tiles.length > 0) {
                    e.preventDefault();
                    const firstTile = tiles[0];
                    firstTile.focus();
                    // Scroll into view
                    firstTile.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    // Update preview for first tile
                    const firstImageId = firstTile.getAttribute('data-image-id');
                    if (firstImageId && files[firstImageId]) {
                        handleImageFocus(files[firstImageId], firstImageId);
                    }
                    // If it was ArrowDown or ArrowRight, navigate to next tile
                    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                        // Use setTimeout to ensure focus is applied first
                        setTimeout(() => {
                            navigateToTile(e.key);
                        }, 0);
                    }
                }
                return;
            }
            
            // Escape to unfocus
            if (e.key === 'Escape') {
                const activeElement = document.activeElement;
                if (activeElement && activeElement.hasAttribute('data-resource-tile')) {
                    e.preventDefault();
                    activeElement.blur();
                    // Clear preview when unfocusing
                    setPreviewFile(null);
                    setPreviewImageId(null);
                    return;
                }
            }
            
            // Ctrl/Cmd + A to deselect all (if there are selections)
            if ((e.ctrlKey || e.metaKey) && e.key === 'a' && selectedImages.size > 0) {
                e.preventDefault();
                handleDeselectAll();
            }
        };
        
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleDeselectAll, selectedImages.size, navigateToTile, isAuthenticated]);
    
    // Show loading state while checking authentication
    if (authChecking) {
        return (
            <div className="h-screen w-screen bg-gray-100 flex items-center justify-center">
                <Helmet>
                    <title>Resources</title>
                    <meta name="robots" content="noindex, nofollow" />
                </Helmet>
                <div className="text-gray-400">Loading...</div>
            </div>
        );
    }
    
    // Not authenticated - this shouldn't normally be seen due to redirect
    if (!isAuthenticated) {
        return null;
    }
    
    return (
        <section
            className={cx(
                'flex-1 min-h-0 flex flex-col relative h-screen overflow-hidden'
            )}
            aria-label="Resources"
            style={{
                backgroundColor: themeBackground,
                color: themeText,
            }}
        >
            <Helmet title="Resources" />
            
            {/* Sticky Header */}
            <header className="sticky top-0 z-50 border-b bg-white/95 backdrop-blur-sm shadow-sm">
                <div className="w-full px-3 sm:px-4 md:px-6 py-2 sm:py-3 md:py-4">
                    {/* Single line layout on all screens */}
                    <div className="flex items-center justify-between gap-2 sm:gap-4">
                        {/* Logo and Title */}
                        <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                            {logoUrl && (
                                <img 
                                    src={logoUrl} 
                                    alt="Logo" 
                                    className="h-5 sm:h-6 md:h-8 w-auto"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        setLogoUrl(null);
                                    }}
                                />
                            )}
                            <h1 className="hidden sm:block text-lg sm:text-xl md:text-2xl font-bold">Resources</h1>
                        </div>
                        
                        {/* Right side: Deselect All, Stats, Download buttons */}
                        <div className="flex items-center gap-1.5 sm:gap-3 md:gap-4 flex-1 justify-end flex-wrap">
                            {/* Stats - hidden on very small screens, shown on sm+ */}
                            <div className="hidden sm:block text-xs sm:text-sm opacity-75 whitespace-nowrap">
                                {selectionStats.count > 0 ? (
                                    <>
                                        {selectionStats.count} image{selectionStats.count !== 1 ? 's' : ''} selected
                                        {selectionStats.totalSize > 0 && (
                                            <span className="hidden md:inline"> • {formatFileSize(selectionStats.totalSize)}</span>
                                        )}
                                    </>
                                ) : (
                                    'Select images to download'
                                )}
                            </div>
                            
                            {/* Mobile: Show count only */}
                            <div className="sm:hidden text-xs opacity-75 whitespace-nowrap">
                                {selectionStats.count > 0 ? (
                                    <>{selectionStats.count} selected</>
                                ) : (
                                    ''
                                )}
                            </div>
                            
                            {/* Deselect All - smaller on mobile */}
                            {selectedImages.size > 0 && (
                                <button
                                    onClick={handleDeselectAll}
                                    tabIndex={-1}
                                    className="text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-1.5 border rounded hover:bg-gray-100 transition-colors whitespace-nowrap"
                                >
                                    <span className="hidden sm:inline">Deselect All</span>
                                    <span className="sm:hidden">Clear</span>
                                </button>
                            )}
                            
                            {/* Download buttons - compact on mobile */}
                            <div className="flex gap-1.5 sm:gap-2">
                                <button
                                    onClick={handleDownloadZip}
                                    disabled={selectedImages.size === 0 || isDownloading || isGeneratingPdf}
                                    tabIndex={-1}
                                    className={cx(
                                        'px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded transition-colors flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm',
                                        selectedImages.size === 0 || isDownloading || isGeneratingPdf
                                            ? 'opacity-50 cursor-not-allowed bg-gray-300'
                                            : 'bg-blue-600 text-white hover:bg-blue-700'
                                    )}
                                >
                                    {isDownloading ? (
                                        <>
                                            <span className="animate-spin text-xs sm:text-sm">⏳</span>
                                            <span className="hidden sm:inline">Downloading...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="hidden sm:inline">Download ZIP</span>
                                            <span className="sm:hidden">ZIP</span>
                                        </>
                                    )}
                                </button>
                                <button
                                    onClick={handleGeneratePdf}
                                    disabled={selectedImages.size === 0 || isDownloading || isGeneratingPdf}
                                    tabIndex={-1}
                                    className={cx(
                                        'px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded transition-colors flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm',
                                        selectedImages.size === 0 || isDownloading || isGeneratingPdf
                                            ? 'opacity-50 cursor-not-allowed bg-gray-300'
                                            : 'bg-red-600 text-white hover:bg-red-700'
                                    )}
                                >
                                    {isGeneratingPdf ? (
                                        <>
                                            <span className="animate-spin text-xs sm:text-sm">⏳</span>
                                            <span className="hidden sm:inline">Generating...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="hidden sm:inline">Generate PDF</span>
                                            <span className="sm:hidden">PDF</span>
                                        </>
                                    )}
                                </button>
                            </div>
                            
                            {/* Logout button */}
                            <button
                                onClick={handleLogout}
                                tabIndex={-1}
                                className="text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors whitespace-nowrap"
                                title="Logout"
                            >
                                <span className="hidden sm:inline">Logout</span>
                                <span className="sm:hidden">✕</span>
                            </button>
                        </div>
                    </div>
                </div>
            </header>
            
            {/* Split View Layout */}
            <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
                {/* Left Preview Panel - Hidden on mobile, shown on large screens */}
                <aside className="hidden lg:flex w-full lg:w-1/2 border-r bg-gray-50 flex flex-col overflow-hidden">
                    {previewFile ? (
                        <div className="w-full h-full flex flex-col items-center justify-center p-8">
                            <div className="relative w-full h-full max-w-4xl max-h-full flex items-center justify-center">
                                {getPreviewImageUrl(previewFile) ? (
                                    <img
                                        src={`/${getPreviewImageUrl(previewFile)}`}
                                        alt={previewFile.description || 'Preview'}
                                        className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                                    />
                                ) : (
                                    <div className="text-gray-400 text-sm">Image not available</div>
                                )}
                            </div>
                            {previewFile.description && (
                                <div className="mt-4 text-center max-w-2xl">
                                    <p className="text-sm opacity-90">{previewFile.description}</p>
                                </div>
                            )}
                        </div>
                    ) : selectedImages.size > 0 ? (
                        <div className="w-full h-full flex flex-col p-4">
                            <h2 className="text-lg font-semibold mb-3 flex-shrink-0">
                                Selected ({selectedImages.size} {selectedImages.size === 1 ? 'image' : 'images'})
                            </h2>
                            <div className="flex-1 overflow-hidden">
                                <div 
                                    className="h-full grid gap-1.5 content-start"
                                    style={{
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                                        gridAutoRows: '80px'
                                    }}
                                >
                                    {Array.from(selectedImages).map(imageId => {
                                        const file = files[imageId];
                                        if (!file) return null;
                                        
                                        // Use getBestAvailableUrl for proper fallback handling
                                        const thumbnailUrl = getBestAvailableUrl(file, 480);
                                        
                                        return (
                                            <div
                                                key={imageId}
                                                className="border rounded overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow relative"
                                            >
                                                {thumbnailUrl ? (
                                                    <div className="w-full h-full bg-gray-100">
                                                        <img
                                                            src={`/${thumbnailUrl}`}
                                                            alt={file.description || ''}
                                                            className="w-full h-full object-cover"
                                                            loading="lazy"
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="w-full h-full bg-gray-200 flex items-center justify-center text-[8px] opacity-50">
                                                        No image
                                                    </div>
                                                )}
                                                {/* Remove button in top-right corner */}
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleImageSelect(imageId, false);
                                                    }}
                                                    tabIndex={-1}
                                                    className="absolute top-1 right-1 w-5 h-5 bg-black/30 hover:bg-black/50 text-white rounded-sm flex items-center justify-center text-xs font-medium transition-colors z-10 backdrop-blur-sm"
                                                    title="Remove from selection"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center opacity-60 w-full flex items-center justify-center h-full p-8">
                            <div className="max-w-md space-y-4">
                                {/* Mouse instructions */}
                                <div className="flex items-center justify-center gap-2 mb-3">
                                    <span className="text-2xl">🖱️</span>
                                    <div className="text-left">
                                        <div className="text-sm font-medium">Hover over an image to preview</div>
                                        <div className="text-xs opacity-75 mt-0.5">or select images to see them here</div>
                                    </div>
                                </div>
                                
                                {/* Keyboard instructions */}
                                <div className="flex items-center justify-center gap-2">
                                    <span className="text-2xl">⌨️</span>
                                    <div className="text-left">
                                        <div className="text-sm font-medium">Use arrow keys to navigate</div>
                                        <div className="text-xs opacity-75 mt-0.5">
                                            <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">↑</kbd>
                                            {' '}
                                            <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">↓</kbd>
                                            {' '}
                                            <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">←</kbd>
                                            {' '}
                                            <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">→</kbd>
                                            {' '}to move • <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">Esc</kbd> to unfocus
                                        </div>
                                        <div className="text-xs opacity-75 mt-1.5">
                                            <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">Space</kbd>
                                            {' '}or{' '}
                                            <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-xs">Enter</kbd>
                                            {' '}to select
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </aside>
                
                {/* Right Content Panel - Scrollable */}
                <main className="w-full lg:w-1/2 overflow-y-auto">
                    <div className="p-6">
                        {/* Search input */}
                        <div className="mb-6">
                            <div className="relative">
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search by page name, image description, or ID..."
                                    className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    tabIndex={-1}
                                />
                                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                                    🔍
                                </span>
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery('')}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        tabIndex={-1}
                                        aria-label="Clear search"
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                            {searchQuery && (
                                <div className="mt-2 text-sm text-gray-600">
                                    Found {filteredPageData.reduce((sum, page) => sum + page.images.length, 0)} image{filteredPageData.reduce((sum, page) => sum + page.images.length, 0) !== 1 ? 's' : ''} in {filteredPageData.length} page{filteredPageData.length !== 1 ? 's' : ''}
                                </div>
                            )}
                        </div>
                        
                        {/* Page groups */}
                        <div className="space-y-4">
                            {filteredPageData.length > 0 ? (
                                filteredPageData.map(page => {
                                    // Check if selecting this page would exceed the 50 image limit
                                    const wouldExceedLimit = selectedImages.size + page.imageIds.length > 50;
                                    const canSelectPage = !wouldExceedLimit;
                                    
                                    return (
                                    <ResourcePageGroup
                                        key={page.pageId}
                                        page={page}
                                        selectedPages={selectedPages}
                                        selectedImages={selectedImages}
                                        onPageSelect={handlePageSelect}
                                        onImageSelect={handleImageSelect}
                                        onImageHover={handleImageHover}
                                        onImageFocus={handleImageFocus}
                                        webpSizes={webpSizes}
                                        canSelectPage={canSelectPage}
                                    />
                                    );
                                })
                            ) : (
                                <div className="text-center py-16">
                                    <div className="opacity-50 space-y-2">
                                        <div className="text-4xl mb-4">📷</div>
                                        <div className="text-lg font-medium">
                                            {searchQuery ? 'No images found matching your search' : 'No images found'}
                                        </div>
                                        <div className="text-sm">
                                            {searchQuery ? (
                                                <>Try a different search term or <button onClick={() => setSearchQuery('')} className="text-blue-600 hover:underline">clear the search</button></>
                                            ) : (
                                                'Images will appear here once they are added to pages.'
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </main>
            </div>
        </section>
    );
}

export default ResourcesPage;
